import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { NavLeaf } from '../nav.model'
import { AdminPrefsService } from '../admin-prefs.service'
import { NavMatch, NavigationService } from '../navigation.service'

interface PaletteRow {
  match: NavMatch
  /** Position in the flattened list, so arrow keys can cross section boundaries. */
  index: number
}

interface PaletteSection {
  title: string
  rows: PaletteRow[]
}

/**
 * Jump-to-page search over the whole menu.
 *
 * Grouping made the sidebar scannable, but the fastest path to a page an admin
 * already knows the name of is typing it. With 40 destinations behind six
 * collapsed sections, this is what keeps the depth from costing anything.
 */
@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
})
export class CommandPaletteComponent {
  @Input() set visible(value: boolean) {
    this.isVisible = value
    if (value) this.reset()
  }
  @Output() visibleChange = new EventEmitter<boolean>()
  /** Lets the sidebar's pinned section re-read without polling the store. */
  @Output() favoritesChanged = new EventEmitter<void>()

  isVisible = false
  query = ''
  activeIndex = 0
  sections: PaletteSection[] = []
  /**
   * Kept alongside `sections` rather than derived in a getter: the template asks
   * which row is active once per row, and re-flattening on each of those calls
   * would make rendering quadratic in the number of results.
   */
  flatResults: NavMatch[] = []

  @ViewChild('search') private searchInput?: ElementRef<HTMLInputElement>

  constructor(
    private nav: NavigationService,
    private prefs: AdminPrefsService,
    private router: Router,
  ) {}

  isFavorite(match: NavMatch): boolean {
    return this.prefs.isFavorite(match.leaf.id)
  }

  /** Stops the click from also navigating — the star sits inside the result row. */
  toggleFavorite(match: NavMatch, event: MouseEvent): void {
    event.stopPropagation()
    this.prefs.toggleFavorite(match.leaf.id)
    this.favoritesChanged.emit()
    if (!this.query.trim()) this.setSections(this.defaultSections())
  }

  /**
   * Deferred a tick: nz-modal renders `*nzModalContent` lazily, so the input does
   * not exist yet when nzAfterOpen fires.
   */
  focusSearch(): void {
    setTimeout(() => this.searchInput?.nativeElement.focus())
  }

  onQueryChange(): void {
    this.activeIndex = 0
    this.setSections(this.query.trim() ? this.searchSections() : this.defaultSections())
  }

  close(): void {
    this.isVisible = false
    this.visibleChange.emit(false)
  }

  go(match: NavMatch): void {
    this.close()
    this.router.navigate([match.leaf.route])
  }

  /** The `path` doubles as a breadcrumb, so a result reads as "section / page". */
  trail(match: NavMatch): string {
    return match.path.map((group) => group.label).join(' / ')
  }

  onKeydown(event: KeyboardEvent): void {
    const results = this.flatResults
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.move(1, results.length)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.move(-1, results.length)
        break
      case 'Enter':
        event.preventDefault()
        if (results[this.activeIndex]) this.go(results[this.activeIndex])
        break
      case 'Escape':
        event.preventDefault()
        this.close()
        break
    }
  }

  private setSections(sections: PaletteSection[]): void {
    let index = 0
    this.sections = sections.map((section) => ({
      title: section.title,
      rows: section.rows.map((row) => ({ ...row, index: index++ })),
    }))
    this.flatResults = this.sections.flatMap((section) => section.rows.map((row) => row.match))
  }

  private move(delta: number, length: number): void {
    if (!length) return
    // Wrap around rather than stopping at the ends — with a short result list,
    // holding one arrow key should cycle instead of dead-ending.
    this.activeIndex = (this.activeIndex + delta + length) % length
    document
      .getElementById(`palette-item-${this.activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' })
  }

  private reset(): void {
    this.query = ''
    this.activeIndex = 0
    this.setSections(this.defaultSections())
  }

  private searchSections(): PaletteSection[] {
    const rows = this.toRows(this.nav.search(this.query))
    return rows.length ? [{ title: 'النتائج', rows }] : []
  }

  /**
   * With no query, the palette is a shortcut list rather than a search box:
   * what this admin pinned, then where they have just been.
   */
  private defaultSections(): PaletteSection[] {
    const sections: PaletteSection[] = []
    const favorites = this.toMatches(this.nav.favorites)
    const recents = this.toMatches(this.nav.recents)

    if (favorites.length) sections.push({ title: 'المفضلة', rows: this.toRows(favorites) })
    if (recents.length) sections.push({ title: 'آخر الزيارات', rows: this.toRows(recents) })
    sections.push({ title: 'كل الصفحات', rows: this.toRows(this.nav.matches) })
    return sections
  }

  /** Indices are assigned in setSections once the full order is known. */
  private toRows(matches: NavMatch[]): PaletteRow[] {
    return matches.map((match) => ({ match, index: 0 }))
  }

  private toMatches(leaves: NavLeaf[]): NavMatch[] {
    const byId = new Map(this.nav.matches.map((match) => [match.leaf.id, match]))
    return leaves.map((leaf) => byId.get(leaf.id)).filter((m): m is NavMatch => !!m)
  }
}
